import type {
  AuditReceipt,
  HumanResponseDecision,
  ReadinessOverride,
  RecoveryVerification,
  ResponseOption,
  ScenarioStage,
} from "../domain";
import {
  initialScenarioState,
  type ScenarioState,
} from "./scenarioMachine.ts";

export const SCENARIO_STORAGE_KEY = "croc-ops:guided-scenario:v1";

type ScenarioEnvelope = {
  version: 1;
  state: ScenarioState;
};

const scenarioStages = new Set<ScenarioStage>([
  "calm",
  "detected",
  "investigating",
  "identified",
  "mitigating",
  "monitoring",
  "recovered",
]);
const responseIds = new Set<ResponseOption["id"]>(["rollback", "reroute", "observe"]);
const overrideRuleIds = new Set(["redundancy-known", "maintenance-window"]);
const receiptKinds = new Set<AuditReceipt["kind"]>([
  "scenario",
  "evidence",
  "acknowledgement",
  "assignment",
  "override",
  "decision",
  "verification",
  "communication",
  "report",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isReceipt(value: unknown): value is AuditReceipt {
  return isRecord(value)
    && typeof value.id === "string"
    && typeof value.occurredAt === "string"
    && typeof value.actor === "string"
    && typeof value.action === "string"
    && typeof value.recordId === "string"
    && typeof value.reason === "string"
    && typeof value.outcome === "string"
    && typeof value.kind === "string"
    && receiptKinds.has(value.kind as AuditReceipt["kind"]);
}

function isOverride(value: unknown): value is ReadinessOverride {
  return isRecord(value)
    && typeof value.ruleId === "string"
    && overrideRuleIds.has(value.ruleId)
    && typeof value.actor === "string"
    && typeof value.reason === "string";
}

function isHumanDecision(value: unknown): value is HumanResponseDecision | null {
  if (value === null) return true;
  return isRecord(value)
    && (value.decision === "approved" || value.decision === "rejected")
    && typeof value.responseId === "string"
    && responseIds.has(value.responseId as ResponseOption["id"])
    && typeof value.approverId === "string"
    && typeof value.approverName === "string";
}

function isRecoveryVerification(value: unknown): value is RecoveryVerification | null {
  if (value === null) return true;
  return isRecord(value)
    && typeof value.serviceRecovered === "boolean"
    && value.endpointId === "TEL-0318"
    && typeof value.endpointReregistered === "boolean"
    && typeof value.verifiedBy === "string";
}

function isScenarioState(value: unknown): value is ScenarioState {
  if (!isRecord(value)) return false;
  const owner = value.assignedOwner;
  const ownerValid = owner === null
    || (
      isRecord(owner)
      && typeof owner.id === "string"
      && typeof owner.name === "string"
    );

  return typeof value.stage === "string"
    && scenarioStages.has(value.stage as ScenarioStage)
    && typeof value.selectedTraceNodeId === "string"
    && isStringArray(value.linkedEvidenceIds)
    && (
      value.decisionId === null
      || (
        typeof value.decisionId === "string"
        && responseIds.has(value.decisionId as ResponseOption["id"])
      )
    )
    && (value.acknowledgedBy === null || typeof value.acknowledgedBy === "string")
    && ownerValid
    && Array.isArray(value.readinessOverrides)
    && value.readinessOverrides.every(isOverride)
    && isHumanDecision(value.humanDecision)
    && isRecoveryVerification(value.recoveryVerification)
    && Array.isArray(value.receipts)
    && value.receipts.every(isReceipt)
    && typeof value.drawerOpen === "boolean"
    && typeof value.guideOpen === "boolean"
    && typeof value.guideStep === "number";
}

export function serializeScenarioState(state: ScenarioState): string {
  const envelope: ScenarioEnvelope = {
    version: 1,
    state,
  };
  return JSON.stringify(envelope);
}

export function parseScenarioState(value: string | null): ScenarioState {
  if (!value) return initialScenarioState;
  try {
    const envelope: unknown = JSON.parse(value);
    if (
      !isRecord(envelope)
      || envelope.version !== 1
      || !isScenarioState(envelope.state)
    ) {
      return initialScenarioState;
    }
    return {
      ...envelope.state,
      drawerOpen: false,
      guideOpen: false,
    };
  } catch {
    return initialScenarioState;
  }
}

export function readPersistedScenarioState(): ScenarioState {
  if (typeof window === "undefined") return initialScenarioState;
  try {
    return parseScenarioState(window.localStorage.getItem(SCENARIO_STORAGE_KEY));
  } catch {
    return initialScenarioState;
  }
}

export function persistScenarioState(state: ScenarioState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SCENARIO_STORAGE_KEY, serializeScenarioState(state));
  } catch {
    // The simulation remains usable in-memory when storage is unavailable.
  }
}
